import React, { Component } from 'react';
import API_BASE_URL from '../config/api';
import './ClientLogin.css';

class ClientLogin extends Component {
  state = {
    mode: 'login',
    username: '',
    password: '',
    errorMessage: '',
    infoMessage: '',
    loading: false,
    requestFullName: '',
    requestEmail: '',
    requestCompany: '',
    requestMessage: '',
  };

  handleChange = (e) => {
    this.setState({
      [e.target.name]: e.target.value,
      errorMessage: '',
      infoMessage: '',
    });
  };

  setMode = (mode) => {
    this.setState({
      mode,
      errorMessage: '',
      infoMessage: '',
      loading: false,
    });
  };

  isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  readResponseJson = async (response) => {
    const text = await response.text();
    if (!text) {
      return {};
    }

    try {
      return JSON.parse(text);
    } catch (error) {
      return {
        status: 'fail',
        message: `Unexpected server response (${response.status})`,
      };
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();

    const { username, password } = this.state;

    if (!username.trim() || !password.trim()) {
      this.setState({
        errorMessage: 'Please enter both username and password.',
      });
      return;
    }

    this.setState({ loading: true, errorMessage: '' });

    try {
      const loginResponse = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: username.trim(),
          password: password.trim(),
        }),
      });

      const loginData = await this.readResponseJson(loginResponse);

      if (!(loginResponse.ok && loginData.status === 'success')) {
        this.setState({
          errorMessage:
            loginData.message || 'Unable to sign in. Please contact administrator.',
        });
        return;
      }

      localStorage.setItem('authToken', loginData.accessToken);
      localStorage.setItem('username', loginData.username);
      localStorage.setItem('role', loginData.role || 'guest');
      localStorage.removeItem('is2FAVerified');

      const codeResponse = await fetch(`${API_BASE_URL}/api/send-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${loginData.accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          username: loginData.username,
        }),
      });

      const codeData = await this.readResponseJson(codeResponse);

      if (!(codeResponse.ok && codeData.status === 'success')) {
        this.setState({
          errorMessage: codeData.message || 'Failed to send verification code.',
        });
        return;
      }

      window.location.replace('/verify');
    } catch (error) {
      console.error('Login error:', error);
      this.setState({
        errorMessage: 'Server error. Please try again later.',
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleRequestAccess = async (e) => {
    e.preventDefault();

    const {
      requestFullName,
      requestEmail,
      requestCompany,
      requestMessage,
    } = this.state;

    if (!requestFullName.trim() || !requestEmail.trim()) {
      this.setState({
        errorMessage: 'Please enter your name and email address.',
      });
      return;
    }

    if (!this.isValidEmail(requestEmail)) {
      this.setState({
        errorMessage: 'Please enter a valid email address in the format name@example.com.',
      });
      return;
    }

    this.setState({ loading: true, errorMessage: '', infoMessage: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/access-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: requestFullName.trim(),
          email: requestEmail.trim(),
          company: requestCompany.trim(),
          message: requestMessage.trim(),
          requestedRole: 'guest',
        }),
      });

      const data = await this.readResponseJson(response);

      if (!(response.ok && data.status === 'success')) {
        this.setState({
          errorMessage: data.message || 'Unable to submit your request right now.',
        });
        return;
      }

      this.setState({
        requestFullName: '',
        requestEmail: '',
        requestCompany: '',
        requestMessage: '',
        infoMessage: data.message || 'Your access request has been submitted.',
        mode: 'login',
      });
    } catch (error) {
      console.error('Access request error:', error);
      this.setState({
        errorMessage: 'Server error. Please try again later.',
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      mode,
      username,
      password,
      errorMessage,
      infoMessage,
      loading,
      requestFullName,
      requestEmail,
      requestCompany,
      requestMessage,
    } = this.state;

    return (
      <div className="login-page-wrap">
        <div className="login-card">
          <div className="login-mode-switch">
            <button
              type="button"
              className={`login-mode-btn ${mode === 'login' ? 'active' : ''}`}
              onClick={() => this.setMode('login')}
            >
              Sign In
            </button>
            <button
              type="button"
              className={`login-mode-btn ${mode === 'request' ? 'active' : ''}`}
              onClick={() => this.setMode('request')}
            >
              Request Access
            </button>
          </div>

          <p className="login-label">
            {mode === 'login' ? 'CLIENT PORTAL' : 'NEW GUEST REQUEST'}
          </p>

          <h1>{mode === 'login' ? 'Access Your Workspace' : 'Request Guest Registration'}</h1>

          <p className="login-subtext">
            {mode === 'login'
              ? 'Sign in to access confidential project information, reports, and collaboration tools.'
              : 'New visitors can request access here. An administrator will review the request and create the account.'}
          </p>

          {mode === 'login' ? (
            <form onSubmit={this.handleSubmit} className="login-form">
              <input
                type="text"
                name="username"
                placeholder="User ID"
                value={username}
                onChange={this.handleChange}
                autoFocus
              />

              <input
                type="password"
                name="password"
                placeholder="Password"
                value={password}
                onChange={this.handleChange}
              />

              {errorMessage && <div className="login-error">{errorMessage}</div>}
              {infoMessage && <div className="login-info">{infoMessage}</div>}

              <button type="submit" disabled={loading}>
                {loading ? 'Signing In...' : 'Enter Portal'}
              </button>
            </form>
          ) : (
            <form onSubmit={this.handleRequestAccess} className="login-form">
              <input
                type="text"
                name="requestFullName"
                placeholder="Full name"
                value={requestFullName}
                onChange={this.handleChange}
                autoFocus
              />

              <input
                type="text"
                name="requestEmail"
                placeholder="Business email"
                value={requestEmail}
                onChange={this.handleChange}
                inputMode="email"
                autoComplete="email"
                spellCheck="false"
                lang="en"
              />

              <input
                type="text"
                name="requestCompany"
                placeholder="Company / organization"
                value={requestCompany}
                onChange={this.handleChange}
              />

              <textarea
                name="requestMessage"
                placeholder="Tell us briefly what access you need"
                value={requestMessage}
                onChange={this.handleChange}
                rows={4}
              />

              {errorMessage && <div className="login-error">{errorMessage}</div>}
              {infoMessage && <div className="login-info">{infoMessage}</div>}

              <button type="submit" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }
}

export default ClientLogin;
